import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../assets/LogoInsurcloud_NoBackground.png';
import DeloitteLogo from '../../assets/Deloitte_LogoNoBackground.png';
import { API_ENDPOINTS } from '../../config/apiConfig.js';
import './Welcome.css';

const VPN_CHECK_URL = `${API_ENDPOINTS.VPN_CHECK}/vpn-check`;
const VPN_TIMEOUT_MS = 8000;

export default function Welcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vpnStatus, setVpnStatus] = useState('checking');

  const checkVpn = useCallback(async () => {
    setVpnStatus('checking');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), VPN_TIMEOUT_MS);
    try {
      const [res] = await Promise.all([
        fetch(VPN_CHECK_URL, { signal: controller.signal }),
        new Promise(resolve => setTimeout(resolve, 2000)),
      ]);
      clearTimeout(timeoutId);
      if (res.ok) {
        navigate('/control');
      } else {
        setVpnStatus('error');
      }
    } catch {
      clearTimeout(timeoutId);
      setVpnStatus('error');
    }
  }, [navigate]);

  useEffect(() => {
    checkVpn();
  }, [checkVpn]);

  const userName = user?.name || 'User';

  return (
    <div className="welcome-screen">
      {/* Background effects */}
      <div className="welcome-bg__grid" />
      <div className="welcome-bg__orb welcome-bg__orb--1" />
      <div className="welcome-bg__orb welcome-bg__orb--2" />
      <div className="welcome-bg__orb welcome-bg__orb--3" />
      <div className="welcome-bg__particles">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="welcome-bg__particle" />
        ))}
      </div>

      {/* Central card */}
      <div className="welcome-card">
        <img src={Logo} alt="InsurCloud" className="welcome-card__logo" />

        <div className="welcome-card__avatar">
          <i className="bi bi-person-fill welcome-card__avatar-icon" />
        </div>

        <div className="welcome-card__greeting">Welcome back</div>
        <h2 className="welcome-card__name">{userName}<span className="welcome-card__dot">.</span></h2>
        <p className="welcome-card__subtitle">IC Control Tower</p>

        <div className="welcome-card__status">
          {vpnStatus === 'checking' && (
            <div className="welcome-card__checking">
              <div className="welcome-card__progress-track">
                <div className="welcome-card__progress-bar" />
              </div>
              <div className="welcome-card__checking-text">
                <i className="bi bi-arrow-repeat" />
                <span>Connecting to IC Hub VPN...</span>
              </div>
            </div>
          )}

          {vpnStatus === 'error' && (
            <div className="welcome-card__error">
              <div className="welcome-card__error-bar" />
              <div className="welcome-card__error-text">
                <i className="bi bi-exclamation-triangle-fill" />
                <span>Connect to the IC Hub VPN to continue.</span>
              </div>
              <button className="welcome-card__retry" onClick={checkVpn}>
                <i className="bi bi-arrow-clockwise" />
                <span>Try again</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="welcome-footer">
        <div className="welcome-footer__powered">
          <span className="welcome-footer__powered-text">Powered by</span>
          <img src={DeloitteLogo} alt="Deloitte" className="welcome-footer__powered-logo" />
        </div>
        <div className="welcome-footer__copy">
          Copyright &copy; {new Date().getFullYear()} Deloitte Technology SA. All rights reserved.
        </div>
      </div>
    </div>
  );
}
